import { MemoizedSelector, select, Store } from '@ngrx/store';
import { filter, takeWhile } from 'rxjs/operators';
import {
  OnboardingActionFinalizeOnboarding,
  OnboardingActionOnboardingCompleted,
  OnboardingActionSendStep
} from '../onboarding.actions';
import {
  Onboarding,
  OnboardingSteps,
  OnboardingStepTypes
} from '../onboarding.models';

interface Bullet {
  color: string;
  width: string;
  display: string;
  stepColor: string;
  marginRight: string;
}

export class StepsManager {
  bullets: Array<Bullet> = [];

  private alive = true;
  private currentIndex = 0;
  private onboarding: Onboarding;

  get currentStep(): OnboardingSteps {
    return (
      (this.onboarding &&
        this.onboarding.steps &&
        this.onboarding.steps.length &&
        this.onboarding.steps[this.currentIndex]) ||
      void 0
    );
  }

  get currentStepValuesForMobi(): object {
    return {
      ...this.currentStep.formValue,
      step: this.currentStep.stepUuid
    };
  }

  get displayPrevBtn(): boolean {
    return this.mobiStepIndex(this.currentStep) > 0;
  }

  get displayNextBtn(): boolean {
    return (
      this.currentStep.type === OnboardingStepTypes.FORM && !this.isLastStep()
    );
  }

  get prevBtnColor(): string {
    return this.currentIndex > 0
      ? this.onboarding.steps[this.currentIndex - 1].color
      : this.onboarding.steps[this.currentIndex].color;
  }

  get nextBtnColor(): string {
    return this.currentIndex < this.onboarding.steps.length - 1
      ? this.onboarding.steps[this.currentIndex + 1].color
      : this.onboarding.steps[this.currentIndex].color;
  }

  constructor(
    private store: Store<any>,
    onboardingSelector: MemoizedSelector<object, Onboarding>
  ) {
    this.store
      .pipe(
        select(onboardingSelector),
        filter(onboarding => !!onboarding),
        takeWhile(() => this.alive)
      )
      .subscribe(onboarding => {
        this.currentIndex = 0;
        this.onboarding = onboarding;
        this.createBullets();
      });
  }

  destroy(): void {
    this.alive = false;
    this.currentIndex = 0;
    this.onboarding = void 0;
  }

  previousStep(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateBullets();
    }
  }

  submitStep(): void {
    if (
      this.currentStep.type === OnboardingStepTypes.INTRO ||
      this.currentStep.type === OnboardingStepTypes.CONCLUSION
    ) {
      this.nextStep();
    } else {
      this.store.dispatch(new OnboardingActionSendStep(this));
    }
  }

  stepSubmissionSuccess(): void {
    if (this.isLastMobiStep()) {
      this.store.dispatch(new OnboardingActionFinalizeOnboarding(this));
    } else {
      this.nextStep();
    }
  }

  finalizeSubmissionSuccess(): void {
    this.nextStep();
  }

  private nextStep(): void {
    if (this.isLastStep()) {
      this.store.dispatch(new OnboardingActionOnboardingCompleted());
    } else {
      this.currentIndex++;
      this.updateBullets();
    }
  }

  private mobiSteps(): Array<OnboardingSteps> {
    return this.onboarding.steps.filter(
      step =>
        step.type !== OnboardingStepTypes.INTRO &&
        step.type !== OnboardingStepTypes.CONCLUSION
    );
  }

  private mobiStepIndex(desiredStep: OnboardingSteps): number {
    return this.mobiSteps().indexOf(desiredStep);
  }

  private isLastStep(desiredStep = this.currentStep): boolean {
    return [...this.onboarding.steps].reverse().indexOf(desiredStep) === 0;
  }

  private isLastMobiStep(desiredStep = this.currentStep): boolean {
    return this.mobiSteps().reverse().indexOf(desiredStep) === 0;
  }

  private createBullets(): void {
    this.bullets =
      this.onboarding && this.onboarding.steps
        ? this.onboarding.steps.map(step => ({
            color: '',
            width: '',
            stepColor: step.color,
            marginRight: '20px',
            display:
              step.type === OnboardingStepTypes.INTRO ||
              step.type === OnboardingStepTypes.CONCLUSION
                ? 'none'
                : 'block'
          }))
        : [];

    this.bullets.forEach((step, idx, arr) => {
      if (
        step.display === 'none' ||
        (idx < arr.length - 1 && arr[idx + 1].display === 'none')
      ) {
        step.marginRight = '0';
      }
    });

    this.updateBullets();
  }

  private updateBullets(): void {
    this.bullets.forEach((bullet, idx) => {
      bullet.width = idx === this.currentIndex ? '30px' : '12px';
      bullet.color = idx > this.currentIndex ? '#2e2e2e' : bullet.stepColor;
    });
  }
}
